import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { apiClient, useAuth } from "../../App";
import { toast } from "sonner";
import { formatDateTime } from "../../i18n";
import {
  MessageSquare,
  Plus,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  Clock,
  User,
  Link2,
  ExternalLink,
  Send,
  X,
  Bell,
  Users,
  Loader2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../ui/collapsible";
import ProgramMeetingsPanel from "../meetings/ProgramMeetingsPanel";

const CommunicationsTab = ({ programId, programName }) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTickets, setExpandedTickets] = useState({});
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [filter, setFilter] = useState("all"); // all, open, resolved, tagged_me
  const [users, setUsers] = useState([]);
  const [replyText, setReplyText] = useState({});
  const [submitting, setSubmitting] = useState(false);
  
  // New ticket form state
  const [newTicket, setNewTicket] = useState({
    title: "",
    body: "",
    tagged_user_ids: [],
    links: []
  });
  const [newLinkLabel, setNewLinkLabel] = useState("");
  const [newLinkUrl, setNewLinkUrl] = useState("");

  useEffect(() => {
    fetchTickets();
    fetchUsers();
  }, [programId, filter]);

  const fetchTickets = async () => {
    try {
      let url = `/programs/${programId}/tickets`;
      const params = [];
      if (filter === "open") params.push("status=open");
      if (filter === "resolved") params.push("status=resolved");
      if (filter === "tagged_me") params.push("tagged_me=true");
      if (params.length > 0) url += "?" + params.join("&");
      
      const response = await apiClient.get(url);
      setTickets(response.data);
    } catch (error) {
      console.error("Failed to load tickets:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await apiClient.get("/users");
      setUsers(response.data.filter(u => u.status !== "invited"));
    } catch (error) {
      console.error("Failed to load users:", error);
    }
  };

  const handleCreateTicket = async () => {
    if (!newTicket.title.trim() || !newTicket.body.trim()) {
      toast.error("Title and message are required");
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await apiClient.post(`/programs/${programId}/tickets`, newTicket);
      setTickets([response.data, ...tickets]);
      setShowCreateDialog(false);
      setNewTicket({ title: "", body: "", tagged_user_ids: [], links: [] });
      toast.success("Ticket created");
    } catch (error) {
      toast.error("Failed to create ticket");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddLink = () => {
    if (!newLinkLabel.trim() || !newLinkUrl.trim()) {
      toast.error("Link label and URL are required");
      return;
    }
    setNewTicket({
      ...newTicket,
      links: [...newTicket.links, { label: newLinkLabel, url: newLinkUrl }]
    });
    setNewLinkLabel("");
    setNewLinkUrl("");
  };

  const handleRemoveLink = (index) => {
    const updatedLinks = [...newTicket.links];
    updatedLinks.splice(index, 1);
    setNewTicket({ ...newTicket, links: updatedLinks });
  };

  const handleToggleUser = (userId) => {
    const current = newTicket.tagged_user_ids;
    if (current.includes(userId)) {
      setNewTicket({
        ...newTicket,
        tagged_user_ids: current.filter(id => id !== userId)
      });
    } else {
      setNewTicket({
        ...newTicket,
        tagged_user_ids: [...current, userId]
      });
    }
  };

  const handleMarkRead = async (ticketId) => {
    try {
      await apiClient.patch(`/tickets/${ticketId}/read`);
      setTickets(tickets.map(t => 
        t.id === ticketId ? { ...t, has_unread: false } : t
      ));
    } catch (error) {
      console.error("Failed to mark as read:", error);
    }
  };

  const handleResolve = async (ticketId) => {
    try {
      await apiClient.patch(`/tickets/${ticketId}/resolve`);
      setTickets(tickets.map(t => 
        t.id === ticketId ? { ...t, status: "resolved", resolved_at: new Date().toISOString() } : t
      ));
      toast.success("Ticket resolved");
    } catch (error) {
      toast.error("Failed to resolve ticket");
    }
  };

  const handleReply = async (ticketId) => {
    const body = replyText[ticketId];
    if (!body?.trim()) {
      toast.error("Reply cannot be empty");
      return;
    }
    
    try {
      const response = await apiClient.post(`/tickets/${ticketId}/replies`, { body });
      setTickets(tickets.map(t => {
        if (t.id === ticketId) {
          return {
            ...t,
            replies: [...(t.replies || []), response.data],
            reply_count: (t.reply_count || 0) + 1
          };
        }
        return t;
      }));
      setReplyText({ ...replyText, [ticketId]: "" });
      toast.success("Reply added");
    } catch (error) {
      toast.error("Failed to add reply");
    }
  };

  const toggleExpanded = (ticketId) => {
    const isExpanding = !expandedTickets[ticketId];
    setExpandedTickets({
      ...expandedTickets,
      [ticketId]: isExpanding
    });
    
    // Mark as read when expanding if user is tagged and hasn't read
    const ticket = tickets.find(t => t.id === ticketId);
    if (isExpanding && ticket?.has_unread) {
      handleMarkRead(ticketId);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { 
      month: "short", 
      day: "numeric",
      hour: "numeric",
      minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="communications-tab">
      {/* Client meetings — booked, linked and emailed from HQ */}
      <ProgramMeetingsPanel programId={programId} programName={programName} />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-muted-foreground" />
          <h2 className="text-lg font-semibold">Communications</h2>
          <Badge variant="secondary" className="rounded-full">
            {tickets.filter(t => t.status === "open").length} open
          </Badge>
        </div>
        <Button 
          onClick={() => setShowCreateDialog(true)}
          size="sm"
          className="gap-2"
          data-testid="create-ticket-btn"
        >
          <Plus className="w-4 h-4" />
          New Ticket
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        {["all", "open", "resolved", "tagged_me"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
              filter === f 
                ? "bg-primary text-primary-foreground" 
                : "bg-secondary text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`filter-${f}`}
          >
            {f === "all" ? "All" : f === "open" ? "Open" : f === "resolved" ? "Resolved" : "Tagged Me"}
          </button>
        ))}
      </div>

      {/* Tickets List */}
      {tickets.length === 0 ? (
        <Card className="border border-border">
          <CardContent className="py-12 text-center">
            <MessageSquare className="w-12 h-12 mx-auto text-muted-foreground mb-4 opacity-50" />
            <p className="text-muted-foreground">No tickets yet</p>
            <p className="text-sm text-muted-foreground mt-1">Create a ticket to start a communication thread</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {tickets.map((ticket) => (
            <Card 
              key={ticket.id} 
              className={`border ${ticket.has_unread ? "border-blue-300 bg-blue-50/30" : "border-border"}`}
              data-testid={`ticket-${ticket.id}`}
            >
              <Collapsible 
                open={expandedTickets[ticket.id]} 
                onOpenChange={() => toggleExpanded(ticket.id)}
              >
                <CollapsibleTrigger asChild>
                  <CardHeader className="pb-3 cursor-pointer hover:bg-secondary/30 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {ticket.has_unread && (
                            <div className="w-2 h-2 rounded-full bg-blue-500" />
                          )}
                          <CardTitle className="text-sm font-medium truncate">
                            {ticket.title}
                          </CardTitle>
                          <Badge 
                            variant={ticket.status === "open" ? "default" : "secondary"}
                            className={`rounded-full text-xs ${
                              ticket.status === "open" 
                                ? "bg-green-100 text-green-700 border-green-200" 
                                : "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {ticket.status === "open" ? "Open" : "Resolved"}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {ticket.created_by_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(ticket.created_at)}
                          </span>
                          {ticket.reply_count > 0 && (
                            <span className="flex items-center gap-1">
                              <MessageSquare className="w-3 h-3" />
                              {ticket.reply_count} {ticket.reply_count === 1 ? "reply" : "replies"}
                            </span>
                          )}
                        </div>
                        {ticket.tagged_users?.length > 0 && (
                          <div className="flex items-center gap-1 mt-2">
                            <Users className="w-3 h-3 text-muted-foreground" />
                            <div className="flex flex-wrap gap-1">
                              {ticket.tagged_users.map((tu) => (
                                <span 
                                  key={tu.user_id}
                                  className="px-2 py-0.5 text-xs bg-secondary rounded-full"
                                >
                                  @{tu.name?.split(" ")[0]}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {expandedTickets[ticket.id] ? (
                          <ChevronUp className="w-5 h-5 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-muted-foreground" />
                        )}
                      </div>
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                
                <CollapsibleContent>
                  <CardContent className="pt-0 space-y-4">
                    {/* Message Body */}
                    <div className="p-4 bg-secondary/30 rounded-lg">
                      <p className="text-sm whitespace-pre-wrap">{ticket.body}</p>
                    </div>
                    
                    {/* Links */}
                    {ticket.links?.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                          <Link2 className="w-3 h-3" />
                          Links
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {ticket.links.map((link) => (
                            <a
                              key={link.id}
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-50 text-blue-700 rounded-full hover:bg-blue-100 transition-colors"
                            >
                              <ExternalLink className="w-3 h-3" />
                              {link.label}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Replies */}
                    {ticket.replies?.length > 0 && (
                      <div className="space-y-3 border-t border-border pt-4">
                        <p className="text-xs font-medium text-muted-foreground">Replies</p>
                        {ticket.replies.map((reply) => (
                          <div 
                            key={reply.id}
                            className="p-3 bg-secondary/20 rounded-lg border-l-2 border-primary/30"
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-medium">{reply.user_name}</span>
                              <span className="text-xs text-muted-foreground">
                                {formatDate(reply.created_at)}
                              </span>
                            </div>
                            <p className="text-sm">{reply.body}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Reply Input */}
                    {ticket.status === "open" && (
                      <div className="flex items-start gap-2 pt-2">
                        <Textarea
                          placeholder="Write a reply..."
                          value={replyText[ticket.id] || ""}
                          onChange={(e) => setReplyText({ ...replyText, [ticket.id]: e.target.value })}
                          className="min-h-[60px] text-sm"
                          data-testid={`reply-input-${ticket.id}`}
                        />
                        <Button
                          size="sm"
                          onClick={() => handleReply(ticket.id)}
                          disabled={!replyText[ticket.id]?.trim()}
                          data-testid={`reply-btn-${ticket.id}`}
                        >
                          <Send className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                    
                    {/* Actions */}
                    <div className="flex justify-end gap-2 pt-2 border-t border-border">
                      {ticket.status === "open" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleResolve(ticket.id)}
                          className="gap-1"
                          data-testid={`resolve-btn-${ticket.id}`}
                        >
                          <CheckCircle2 className="w-4 h-4" />
                          Mark Resolved
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Collapsible>
            </Card>
          ))}
        </div>
      )}

      {/* Create Ticket Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Create Communication Ticket</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                placeholder="Brief summary of the communication"
                value={newTicket.title}
                onChange={(e) => setNewTicket({ ...newTicket, title: e.target.value })}
                data-testid="ticket-title-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Message *</Label>
              <Textarea
                placeholder="Full message content..."
                value={newTicket.body}
                onChange={(e) => setNewTicket({ ...newTicket, body: e.target.value })}
                className="min-h-[120px]"
                data-testid="ticket-body-input"
              />
            </div>
            
            {/* Tag Users */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Bell className="w-4 h-4" />
                Tag Users (they will be notified)
              </Label>
              <div className="flex flex-wrap gap-2 max-h-[150px] overflow-y-auto p-2 border rounded-lg">
                {users.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => handleToggleUser(u.id)}
                    className={`px-3 py-1.5 text-xs rounded-full transition-colors ${
                      newTicket.tagged_user_ids.includes(u.id)
                        ? "bg-primary text-primary-foreground"
                        : "bg-secondary hover:bg-secondary/80"
                    }`}
                    data-testid={`tag-user-${u.id}`}
                  >
                    @{u.name}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Links */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Link2 className="w-4 h-4" />
                Links (optional)
              </Label>
              {newTicket.links.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {newTicket.links.map((link, idx) => (
                    <span 
                      key={idx}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded-full"
                    >
                      {link.label}
                      <button 
                        type="button"
                        onClick={() => handleRemoveLink(idx)}
                        className="hover:text-red-500"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <Input
                  placeholder="Link label"
                  value={newLinkLabel}
                  onChange={(e) => setNewLinkLabel(e.target.value)}
                  className="flex-1"
                />
                <Input
                  placeholder="URL"
                  value={newLinkUrl}
                  onChange={(e) => setNewLinkUrl(e.target.value)}
                  className="flex-1"
                />
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={handleAddLink}
                >
                  Add
                </Button>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateTicket}
              disabled={submitting || !newTicket.title.trim() || !newTicket.body.trim()}
              data-testid="submit-ticket-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create Ticket"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CommunicationsTab;
